import { isTopTierDeal } from '@/helpers/deals/isTopTierDeal'

interface DealBadgeTrackingProps {
  deal_badge_value: string
  is_deal_badge_shown: boolean
}

interface FmvBadgeFlag {
  enabled?: boolean
  excludedStorefrontIds?: string[]
  excludedStorefrontSlugs?: string[]
}

/**
 * Get deal badge tracking properties for Segment analytics events.
 * Non-hook version for use in components where the listing might not be available
 * or where hooks cannot be called conditionally.
 *
 * Works with various listing types including OrchestratedListingDetails and SearchResults.
 *
 * @param listing - The listing object containing deal information
 * @param fmvBadgeFlag - The feature flag configuration for the FMV deal badge
 * @returns Object with deal_badge_value (string) and is_deal_badge_shown (boolean)
 */
export const getDealBadgeTrackingProps = (
  listing: unknown,
  fmvBadgeFlag: FmvBadgeFlag
): DealBadgeTrackingProps => {
  if (!listing || typeof listing !== 'object') {
    return {
      deal_badge_value: 'No Badge',
      is_deal_badge_shown: false,
    }
  }

  const listingObj = listing as Record<string, unknown>

  // Safely extract deal property
  const deal =
    'deal' in listingObj && typeof listingObj.deal === 'string'
      ? listingObj.deal
      : null

  // Get storefront ID from either nested structure or top-level property
  let storefrontId: string | undefined
  if (
    'seller' in listingObj &&
    typeof listingObj.seller === 'object' &&
    listingObj.seller !== null
  ) {
    const seller = listingObj.seller as Record<string, unknown>
    if (
      'storefront' in seller &&
      typeof seller.storefront === 'object' &&
      seller.storefront !== null
    ) {
      const storefront = seller.storefront as Record<string, unknown>
      if ('_id' in storefront && typeof storefront._id === 'string') {
        storefrontId = storefront._id
      }
    }
  }
  if (
    !storefrontId &&
    'storefrontId' in listingObj &&
    typeof listingObj.storefrontId === 'string'
  ) {
    storefrontId = listingObj.storefrontId
  }

  // Get storefront slug from either nested structure or top-level property
  let storefrontSlug: string | undefined
  if (
    'seller' in listingObj &&
    typeof listingObj.seller === 'object' &&
    listingObj.seller !== null
  ) {
    const seller = listingObj.seller as Record<string, unknown>
    if (
      'storefront' in seller &&
      typeof seller.storefront === 'object' &&
      seller.storefront !== null
    ) {
      const storefront = seller.storefront as Record<string, unknown>
      if ('slug' in storefront && typeof storefront.slug === 'string') {
        storefrontSlug = storefront.slug
      }
    }
  }
  if (
    !storefrontSlug &&
    'storefrontSlug' in listingObj &&
    typeof listingObj.storefrontSlug === 'string'
  ) {
    storefrontSlug = listingObj.storefrontSlug
  }

  // Check if storefront is excluded by ID
  const isExcludedById =
    fmvBadgeFlag.excludedStorefrontIds &&
    fmvBadgeFlag.excludedStorefrontIds.length > 0 &&
    storefrontId &&
    fmvBadgeFlag.excludedStorefrontIds.includes(storefrontId)

  // Check if storefront is excluded by slug
  const isExcludedBySlug =
    fmvBadgeFlag.excludedStorefrontSlugs &&
    fmvBadgeFlag.excludedStorefrontSlugs.length > 0 &&
    storefrontSlug &&
    fmvBadgeFlag.excludedStorefrontSlugs.includes(storefrontSlug)

  // Determine if badge should be shown based on flag, deal value, and exclusions
  const shouldShowFmvBadge =
    fmvBadgeFlag.enabled &&
    isTopTierDeal(deal) &&
    !isExcludedById &&
    !isExcludedBySlug

  // If badge is not shown, return "No Badge" and false
  if (!shouldShowFmvBadge || !deal) {
    return {
      deal_badge_value: 'No Badge',
      is_deal_badge_shown: false,
    }
  }

  // The badge only shows for the top tier, so deal here is always
  // "Great Price" (DealsEnum.GreatPrice).
  return {
    deal_badge_value: deal,
    is_deal_badge_shown: true,
  }
}
